import React, { useEffect, useState } from 'react'
import SubtabNav from '../Workflow/SubtabNav'
import ChartPreviewWithOptions from '../ChartPreviewWithOptions'
import PreviewSizeControl from './PreviewSizeControl'
import StyleSidebarContent from './StyleSidebarContent'
import WorkflowMobileSheet from './WorkflowMobileSheet'
import { STYLE_TABS } from '../../config/workflow'
import { useDataContext } from '../../contexts/DataContext'
import { useChartContext } from '../../contexts/ChartContext'
import { useUIContext } from '../../contexts/UIContext'
import { onExportNudgeClicked } from '../../gaEvents'
import FeedbackPrompt from '../FeedbackPrompt/FeedbackPrompt'
import OpenInCanvasButton from '../../labs/OpenInCanvasButton'

export default function StyleStep({ actions }) {
  const { filteredData: data, largeDatasetInfo, dataSource } = useDataContext()

  const {
    currentChart,
    mapping,
    visualOptions,
    setVisualOptions,
    rawViz,
    setRawViz,
    mappingLoading,
    setMappingLoading,
    brandTheme,
    chartColorSyncMode,
    chartColorOptionMeta,
  } = useChartContext()

  const {
    previewDimensions,
    previewSizePreset,
    setPreviewSizePreset,
    previewCustomSize,
    setPreviewCustomSize,
    previewSizePresets,
    activeStyleTab,
    setActiveStyleTab,
    previewRendererMode,
    setCurrentStep,
  } = useUIContext()

  const datasetRowCount = largeDatasetInfo.rowCount

  const renderSource = dataSource?.type === 'sample' ? 'sample_data' : dataSource?.type ? 'user_data' : 'unknown'
  const chartId = currentChart?.metadata?.id

  function handleExportNudge() {
    onExportNudgeClicked(chartId)
    setCurrentStep('export')
  }

  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  )

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const handleChange = () => setIsMobileLayout(mediaQuery.matches)

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  if (isMobileLayout) {
    return (
      <WorkflowMobileSheet
        mainContent={
          <ChartPreviewWithOptions
            chart={currentChart}
            dataset={data.dataset}
            dataTypes={data.dataTypes}
            mapping={mapping}
            visualOptions={visualOptions}
            setVisualOptions={setVisualOptions}
            setRawViz={setRawViz}
            datasetRowCount={datasetRowCount}
            previewRendererMode={previewRendererMode}
            loading={mappingLoading}
            setMappingLoading={setMappingLoading}
            brandTheme={brandTheme}
            chartColorSyncMode={chartColorSyncMode}
            hasMappedColorField={chartColorOptionMeta.hasMappedColorField}
            showOptions={false}
            previewClassName="col-12"
          />
        }
        topStrip={
          <PreviewSizeControl
            previewSizePreset={previewSizePreset}
            setPreviewSizePreset={setPreviewSizePreset}
            previewCustomSize={previewCustomSize}
            setPreviewCustomSize={setPreviewCustomSize}
            previewDimensions={previewDimensions}
            presets={previewSizePresets}
          />
        }
        tabs={STYLE_TABS}
        activeTab={activeStyleTab}
        onTabChange={setActiveStyleTab}
        sheetContent={<StyleSidebarContent />}
        actions={actions}
      />
    )
  }

  // Desktop: side-by-side layout (unchanged)
  return (
    <>
      <div className="workflow-step-header">
        <div className="workflow-step-copy">
          <h2>Style</h2>
          <p>
            Apply brand settings and chart options while the preview stays
            visible on the same screen.
          </p>
        </div>
        <div className="workflow-step-actions-desktop">{actions}</div>
      </div>
      <SubtabNav
        tabs={STYLE_TABS}
        activeTab={activeStyleTab}
        onTabChange={setActiveStyleTab}
      />
      <div className="workflow-split">
        <div className="workflow-panel-mobile-open">
          <StyleSidebarContent />
        </div>
        <div className="workflow-panel workflow-panel-preview">
          <ChartPreviewWithOptions
            chart={currentChart}
            dataset={data.dataset}
            dataTypes={data.dataTypes}
            mapping={mapping}
            visualOptions={visualOptions}
            setVisualOptions={setVisualOptions}
            setRawViz={setRawViz}
            datasetRowCount={datasetRowCount}
            previewRendererMode={previewRendererMode}
            loading={mappingLoading}
            setMappingLoading={setMappingLoading}
            brandTheme={brandTheme}
            chartColorSyncMode={chartColorSyncMode}
            hasMappedColorField={chartColorOptionMeta.hasMappedColorField}
            showOptions={false}
            previewClassName="col-12"
            renderSource={renderSource}
          />
          {rawViz && (
            <div className="export-nudge">
              <span className="export-nudge__label">
                {renderSource === 'sample_data'
                  ? 'Customize this or export it now'
                  : 'Looks good? Export your chart'}
              </span>
              <button
                type="button"
                className="export-nudge__cta"
                onClick={handleExportNudge}
              >
                Export as SVG, PNG or JPG →
              </button>
              <OpenInCanvasButton
                chart={currentChart}
                dataset={data.dataset}
                dataTypes={data.dataTypes}
                mapping={mapping}
                visualOptions={visualOptions}
                brandTheme={brandTheme}
                datasetName={dataSource?.datasetTitle}
              />
            </div>
          )}
          <PreviewSizeControl
            previewSizePreset={previewSizePreset}
            setPreviewSizePreset={setPreviewSizePreset}
            previewCustomSize={previewCustomSize}
            setPreviewCustomSize={setPreviewCustomSize}
            previewDimensions={previewDimensions}
            presets={previewSizePresets}
          />
          {rawViz && <FeedbackPrompt chartId={chartId} />}
        </div>
      </div>
      <div className="workflow-step-actions-mobile">{actions}</div>
    </>
  )
}
